import { AtSign, ExternalLink, ShieldCheck, ShieldAlert } from 'lucide-react';
import type { SocialMediaHandles } from '@/types';

interface SocialMediaPanelProps {
    handles?: SocialMediaHandles | null | undefined;
    // `consentSocialMediaReview` — whether the employee agreed to a social-media review.
    consentGiven?: boolean | undefined;
    className?: string | undefined;
}

// Fixed display order; `other` last. Any key may be absent.
const PLATFORM_ORDER: { key: keyof SocialMediaHandles; label: string }[] = [
    { key: 'instagram', label: 'Instagram' },
    { key: 'twitter', label: 'Twitter / X' },
    { key: 'facebook', label: 'Facebook' },
    { key: 'linkedin', label: 'LinkedIn' },
    { key: 'tiktok', label: 'TikTok' },
    { key: 'other', label: 'Other' },
];

// Turn a handle into an openable URL when we can. Employees type these freely —
// "@janedoe", "facebook.com/janedoe", or a full URL — so handle all three.
function toUrl(platform: keyof SocialMediaHandles, raw: string): string | null {
    const value = raw.trim();
    if (!value) return null;
    if (/^https?:\/\//i.test(value)) return value;
    // Looks like a bare domain path ("facebook.com/janedoe", "github.com/x")
    if (/^[\w-]+\.[a-z]{2,}\//i.test(value)) return `https://${value}`;

    const handle = value.replace(/^@/, '');
    if (!handle) return null;
    switch (platform) {
        case 'instagram':
            return `https://instagram.com/${handle}`;
        case 'twitter':
            return `https://x.com/${handle}`;
        case 'facebook':
            return `https://facebook.com/${handle}`;
        case 'linkedin':
            return `https://linkedin.com/in/${handle}`;
        case 'tiktok':
            return `https://tiktok.com/@${handle}`;
        default:
            // `other` is free-form — only link it if it already looked like a URL/domain.
            return null;
    }
}

/**
 * Read-only social-media panel shown to every role that works an investigation
 * (Super Admin, Customer Success, Agent). Surfaces the handles the employee
 * submitted plus whether they consented to a social-media review.
 */
export default function SocialMediaPanel({ handles, consentGiven, className = '' }: SocialMediaPanelProps) {
    const entries = PLATFORM_ORDER.map(({ key, label }) => ({
        key,
        label,
        value: handles?.[key]?.trim() ?? '',
    })).filter((e) => e.value.length > 0);

    return (
        <div className={`rounded-xl border border-[#e0e0e0] p-4 flex flex-col gap-3 ${className}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-sm font-bold text-[#1b1b1b]">
                    <AtSign className="h-4 w-4 text-[#FF6B35]" />
                    Social media
                </h3>
                {/* Consent to social-media review — drives whether the agent may act on these. */}
                {consentGiven === true ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-[11px] font-medium text-green-700">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Review consented
                    </span>
                ) : consentGiven === false ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700">
                        <ShieldAlert className="h-3.5 w-3.5" />
                        Review not consented
                    </span>
                ) : null}
            </div>

            {consentGiven === false ? (
                // The employee did not consent to a social-media review, so the handles
                // are withheld — consent is the permission to act on them.
                <p className="text-sm text-[#9a9a9a]">
                    The employee did not consent to a social media review, so their handles are not shown.
                </p>
            ) : entries.length === 0 ? (
                <p className="text-sm text-[#9a9a9a]">No social media handles provided.</p>
            ) : (
                <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {entries.map(({ key, label, value }) => {
                        const url = toUrl(key, value);
                        return (
                            <li key={key} className="flex flex-col gap-0.5 min-w-0">
                                <span className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">{label}</span>
                                {url ? (
                                    <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 truncate text-sm font-medium text-[#1b1b1b] hover:text-[#FF6B35] hover:underline"
                                        title={value}
                                    >
                                        <span className="truncate">{value}</span>
                                        <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
                                    </a>
                                ) : (
                                    <span className="truncate text-sm font-medium text-[#1b1b1b]" title={value}>
                                        {value}
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
